import React, { useMemo, useState, useEffect, useCallback } from "react";

// --- Data Processing Helpers ---

/**
 * Converts the A2UI data model structure (list of key-value entries) into a standard JavaScript object.
 * Recursively handles valueMap.
 */
const processDataModel = (contents) => {
  if (!contents) return {};
  const model = {};

  if (Array.isArray(contents)) {
    contents.forEach((entry) => {
      const {
        key,
        valueString,
        valueNumber,
        valueBoolean,
        valueMap,
        valueArray,
      } = entry;

      if (valueMap) {
        model[key] = processDataModel(valueMap);
      } else if (valueArray) {
        model[key] = valueArray;
      } else {
        model[key] = valueString ?? valueNumber ?? valueBoolean;
      }
    });
  }
  return model;
};

/**
 * Resolves a value from the data model based on a path or literal.
 */
const resolveValue = (prop, dataModel, context) => {
  if (!prop) return undefined;

  // Handle literals
  if (prop.literalString !== undefined) return prop.literalString;
  if (prop.literalNumber !== undefined) return prop.literalNumber;
  if (prop.literalBoolean !== undefined) return prop.literalBoolean;

  // Handle paths
  if (prop.path) {
    let target = prop.path.startsWith("/") ? dataModel : context || dataModel;
    // Remove leading slash if present for cleaner splitting
    const pathStr = prop.path.startsWith("/")
      ? prop.path.substring(1)
      : prop.path;
    if (!pathStr) return target; // Root path '/' returns the whole model

    const parts = pathStr.split("/");

    for (const part of parts) {
      if (target === undefined || target === null) return undefined;

      if (Array.isArray(target)) {
        // If target is array, part should be index
        const index = parseInt(part, 10);
        target = target[index];
      } else if (typeof target === "object") {
        // If target is object
        if (target[part] !== undefined) {
          target = target[part];
        } else {
          // Fallback: Try numeric index if part is a number
          const index = parseInt(part, 10);
          if (!isNaN(index)) {
            const values = Object.values(target);
            target = values[index];
          } else {
            return undefined;
          }
        }
      } else {
        return undefined;
      }
    }
    return target;
  }

  return undefined;
};

/**
 * Updates a value in the data model at the specified path.
 * Returns a new copy of the data model.
 */
const updateValue = (model, path, value) => {
  if (!path) return model;

  // Only support absolute paths (starting with /) or relative paths if we assume root context for updates for now.
  // In this simple implementation, we'll treat all updates as relative to the root model if they start with /
  // or if they are just keys.

  const pathStr = path.startsWith("/") ? path.substring(1) : path;
  if (!pathStr) return value;

  const parts = pathStr.split("/");

  // Helper to clone and traverse
  const clone = (obj) => {
    if (Array.isArray(obj)) return [...obj];
    if (obj && typeof obj === "object") return { ...obj };
    return obj;
  };

  const newModel = clone(model);
  let current = newModel;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];

    // Auto-vivify if missing
    if (current[part] === undefined) {
      // Look ahead to see if next part is number -> array?
      // For simplicity, default to object.
      current[part] = {};
    }

    current[part] = clone(current[part]);
    current = current[part];
  }

  const lastPart = parts[parts.length - 1];
  current[lastPart] = value;

  return newModel;
};

// --- Component Renderer ---

const A2UIComponent = ({
  id,
  componentMap,
  dataModel,
  context,
  onDataUpdate,
}) => {
  const componentDef = componentMap[id];

  if (!componentDef) {
    // console.warn(`Component with ID ${id} not found.`);
    return null;
  }

  const componentType = Object.keys(componentDef.component)[0];
  const props = componentDef.component[componentType];

  // Helper to resolve children (explicit list or template)
  const renderChildren = (childrenProp) => {
    if (!childrenProp) return null;

    // Case 1: Explicit List of IDs
    if (childrenProp.explicitList) {
      return childrenProp.explicitList.map((childId) => (
        <A2UIComponent
          key={childId}
          id={childId}
          componentMap={componentMap}
          dataModel={dataModel}
          context={context}
          onDataUpdate={onDataUpdate}
        />
      ));
    }

    // Case 2: Template
    if (childrenProp.template) {
      const { componentId, dataBinding } = childrenProp.template;
      // Resolve the data for the template
      const dataList = resolveValue({ path: dataBinding }, dataModel, context);

      if (!dataList) return null;

      // Iterate over data and render the template component
      let items = [];
      if (Array.isArray(dataList)) {
        items = dataList;
      } else if (typeof dataList === "object") {
        items = Object.values(dataList);
      }

      return items.map((item, index) => (
        <A2UIComponent
          key={`${componentId}-${index}`}
          id={componentId}
          componentMap={componentMap}
          dataModel={dataModel}
          context={item} // Pass the item as context for relative paths
          onDataUpdate={onDataUpdate}
        />
      ));
    }

    return null;
  };

  // Common Styles
  const containerStyle = {
    padding: "10px",
    display: "flex",
    gap: "10px",
  };

  switch (componentType) {
    case "Text":
      const textContent = resolveValue(props.text, dataModel, context);
      const usageHint = props.usageHint || "body";
      const textStyles = {
        h1: { fontSize: "2em", fontWeight: "bold", margin: "10px 0" },
        h2: { fontSize: "1.5em", fontWeight: "bold", margin: "8px 0" },
        h3: { fontSize: "1.2em", fontWeight: "bold", margin: "6px 0" },
        h4: { fontSize: "1.1em", fontWeight: "bold", margin: "5px 0" },
        body: { fontSize: "1em", margin: "4px 0" },
        caption: { fontSize: "0.8em", color: "#ccc" },
      };

      return (
        <div style={{ ...textStyles[usageHint], color: "white" }}>
          {textContent}
        </div>
      );

    case "Image":
      const imageUrl = resolveValue(props.url, dataModel, context);
      const width = props.width || "100%";
      return (
        <img
          src={imageUrl}
          alt="content"
          style={{
            maxWidth: "100%",
            width: width,
            borderRadius: "8px",
            objectFit: props.fit || "cover",
          }}
        />
      );

    case "Video":
      const videoUrl = resolveValue(props.url, dataModel, context);
      return (
        <video
          src={videoUrl}
          controls
          style={{
            maxWidth: "100%",
            width: "100%",
            borderRadius: "8px",
            marginTop: "10px",
            marginBottom: "10px",
          }}
        />
      );

    case "Column":
      return (
        <div
          style={{
            ...containerStyle,
            flexDirection: "column",
            alignItems:
              props.alignment === "center"
                ? "center"
                : props.alignment === "end"
                ? "flex-end"
                : "flex-start",
            justifyContent: props.distribution || "flex-start",
          }}
        >
          {renderChildren(props.children)}
        </div>
      );

    case "Row":
      return (
        <div
          style={{
            ...containerStyle,
            flexDirection: "row",
            alignItems: props.alignment || "center",
            justifyContent: props.distribution || "flex-start",
            flexWrap: "wrap",
          }}
        >
          {renderChildren(props.children)}
        </div>
      );

    case "List":
      return (
        <div
          style={{
            display: "flex",
            flexDirection: props.direction === "horizontal" ? "row" : "column",
            gap: "10px",
            width: "100%",
          }}
        >
          {renderChildren(props.children)}
        </div>
      );

    case "Card":
      return (
        <div
          style={{
            padding: "15px",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "12px",
            backgroundColor: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(5px)",
            margin: "5px 0",
          }}
        >
          <A2UIComponent
            id={props.child}
            componentMap={componentMap}
            dataModel={dataModel}
            context={context}
            onDataUpdate={onDataUpdate}
          />
        </div>
      );

    case "Button":
      const buttonLabel = resolveValue(
        props.child ? { path: "unused" } : props.text || props.label,
        dataModel,
        context
      );

      let buttonContent = null;
      if (props.child) {
        buttonContent = (
          <A2UIComponent
            id={props.child}
            componentMap={componentMap}
            dataModel={dataModel}
            context={context}
            onDataUpdate={onDataUpdate}
          />
        );
      } else {
        buttonContent = buttonLabel || "Button";
      }

      const handleAction = () => {
        if (props.action) {
          console.log("Button clicked with action:", props.action);
          // In a real app, this would dispatch an event to the backend.
          // For now, we can just log the resolved context to verify data binding.
          if (props.action.context) {
            const resolvedContext = props.action.context.map((item) => ({
              key: item.key,
              value: resolveValue(item.value, dataModel, context),
            }));
            console.log("Resolved Action Context:", resolvedContext);
            alert(
              `Action: ${props.action.name}\nData: ${JSON.stringify(
                resolvedContext,
                null,
                2
              )}`
            );
          } else {
            alert(`Action: ${props.action.name}`);
          }
        }
      };

      return (
        <button
          onClick={handleAction}
          style={{
            padding: "10px 20px",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            color: "white",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            borderRadius: "20px",
            cursor: "pointer",
            fontSize: "1em",
            transition: "all 0.3s ease",
            marginTop: "10px",
          }}
          onMouseOver={(e) =>
            (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.4)")
          }
          onMouseOut={(e) =>
            (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
          }
        >
          {buttonContent}
        </button>
      );

    case "Divider":
      return (
        <hr
          style={{
            width: "100%",
            borderColor: "rgba(255,255,255,0.2)",
            margin: "10px 0",
          }}
        />
      );

    case "TextField":
      return (
        <div style={{ margin: "10px 0", width: "100%" }}>
          <label
            style={{ display: "block", color: "#ccc", marginBottom: "5px" }}
          >
            {resolveValue(props.label, dataModel, context)}
          </label>
          <input
            type={props.type === "number" ? "number" : "text"}
            value={resolveValue(props.text, dataModel, context) || ""}
            onChange={(e) => {
              if (props.text && props.text.path && onDataUpdate) {
                onDataUpdate(props.text.path, e.target.value);
              }
            }}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #555",
              background: "rgba(0,0,0,0.3)",
              color: "white",
            }}
          />
        </div>
      );

    case "DateTimeInput":
      const dtLabel = resolveValue(props.label, dataModel, context);
      const dtValue = resolveValue(props.value, dataModel, context) || "";

      let inputType = "datetime-local";
      if (props.enableDate && !props.enableTime) inputType = "date";
      if (!props.enableDate && props.enableTime) inputType = "time";

      return (
        <div style={{ margin: "10px 0", width: "100%" }}>
          <label
            style={{ display: "block", color: "#ccc", marginBottom: "5px" }}
          >
            {dtLabel}
          </label>
          <input
            type={inputType}
            value={dtValue}
            onChange={(e) => {
              if (props.value && props.value.path && onDataUpdate) {
                onDataUpdate(props.value.path, e.target.value);
              }
            }}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #555",
              background: "rgba(0,0,0,0.3)",
              color: "white",
            }}
          />
        </div>
      );

    default:
      console.warn(`Unsupported A2UI component type: ${componentType}`);
      return null;
  }
};

const A2UIRenderer = ({ data }) => {
  const { text, a2ui_messages } = useMemo(() => {
    if (!data) return { text: "", a2ui_messages: [] };
    // Handle new structure { text, a2ui_messages }
    if (data.a2ui_messages) return data;
    // Handle legacy structure (raw list or object) - fallback
    if (Array.isArray(data)) return { text: "", a2ui_messages: data };
    return { text: "", a2ui_messages: [data] };
  }, [data]);

  // Build Initial State
  const { componentMap, initialDataModel, rootId } = useMemo(() => {
    let components = {};
    let model = {};
    let root = null;

    if (Array.isArray(a2ui_messages)) {
      a2ui_messages.forEach((msg) => {
        if (msg.surfaceUpdate) {
          msg.surfaceUpdate.components.forEach((comp) => {
            components[comp.id] = comp;
          });
        }
        if (msg.dataModelUpdate) {
          const newModel = processDataModel(msg.dataModelUpdate.contents);
          model = { ...model, ...newModel };
        }
        if (msg.beginRendering) {
          root = msg.beginRendering.root;
        }
      });
    }

    // Fallback if no root explicitly defined
    if (!root && Object.keys(components).length > 0) {
      root = Object.keys(components)[0];
    }

    return { componentMap: components, initialDataModel: model, rootId: root };
  }, [a2ui_messages]);

  // Mutable Data Model State
  const [dataModel, setDataModel] = useState({});

  // Sync state when initial data changes (e.g. new message received)
  useEffect(() => {
    setDataModel(initialDataModel);
  }, [initialDataModel]);

  const handleDataUpdate = useCallback((path, value) => {
    setDataModel((prevModel) => updateValue(prevModel, path, value));
  }, []);

  if (!a2ui_messages || a2ui_messages.length === 0) {
    if (text)
      return <div style={{ color: "white", padding: "20px" }}>{text}</div>;
    return null;
  }

  return (
    <div
      className="a2ui-surface"
      style={{
        width: "100%",
        maxWidth: "800px",
        margin: "0 auto",
        color: "white",
      }}
    >
      {text && (
        <div
          style={{
            marginBottom: "20px",
            padding: "10px",
            backgroundColor: "rgba(0,0,0,0.3)",
            borderRadius: "8px",
          }}
        >
          {text}
        </div>
      )}

      {rootId && componentMap[rootId] ? (
        <A2UIComponent
          id={rootId}
          componentMap={componentMap}
          dataModel={dataModel}
          context={null}
          onDataUpdate={handleDataUpdate}
        />
      ) : (
        <div style={{ padding: "20px", color: "#ff6b6b" }}>
          Error: Could not render UI. No root component found.
        </div>
      )}
    </div>
  );
};

export default A2UIRenderer;
